import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleCheck, Trash2, Check, X, Images } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "@/components/landing/CustomerReviews";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const TABS = ["pending", "approved", "rejected"];

export default function AdminReviews() {
    const [tab, setTab] = useState("pending");
    const [reviews, setReviews] = useState([]);
    const [mode, setMode] = useState("all");

    useEffect(() => {
        api.get("/gallery").then((r) => setMode(r.data.mode)).catch(() => {});
    }, []);

    const changeMode = async (m) => {
        await api.put("/admin/gallery-mode", { mode: m });
        setMode(m);
        toast.success(m === "all" ? "Gallery shows all approved photos" : "Gallery shows only selected photos");
    };

    const toggleGallery = async (r) => {
        await api.patch(`/admin/reviews/${r.id}`, { in_gallery: !r.in_gallery });
        setReviews((rs) => rs.map((x) => (x.id === r.id ? { ...x, in_gallery: !r.in_gallery } : x)));
    };

    const load = () =>
        api.get("/admin/reviews", { params: { status: tab } }).then((r) => setReviews(r.data)).catch(() => setReviews([]));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/reviews/${id}`, { status });
            toast.success(`Review ${status}`);
            setReviews((rs) => rs.filter((r) => r.id !== id));
        } catch {
            toast.error("Update failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this review permanently?")) return;
        await api.delete(`/admin/reviews/${id}`);
        setReviews((rs) => rs.filter((r) => r.id !== id));
        toast.success("Review deleted");
    };

    return (
        <div data-testid="admin-reviews-page" className="space-y-6">
            <div>
                <h1 className="font-serif font-bold text-3xl text-forest-800">Reviews</h1>
                <p className="text-sm text-forest-900/60 mt-1">Approve customer reviews before they appear on product pages.</p>
            </div>
            <div data-testid="gallery-mode" className="bg-white rounded-2xl border border-forest-100 p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Images className="w-5 h-5 text-forest-700" />
                    <div>
                        <p className="font-bold text-sm text-forest-900">Home page Before/After gallery</p>
                        <p className="text-xs text-forest-700/70">Choose which approved review photos appear on the home page.</p>
                    </div>
                </div>
                <div className="flex gap-1 bg-forest-50 rounded-full p-1">
                    {[["all", "All approved photos"], ["selected", "Only selected"]].map(([m, label]) => (
                        <button
                            key={m}
                            data-testid={`gallery-mode-${m}`}
                            onClick={() => changeMode(m)}
                            className={`text-xs font-bold px-4 py-1.5 rounded-full transition-colors ${mode === m ? "bg-forest-800 text-gold-400" : "text-forest-700"}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex gap-2">
                {TABS.map((t) => (
                    <button
                        key={t}
                        data-testid={`reviews-tab-${t}`}
                        onClick={() => setTab(t)}
                        className={`text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${tab === t ? "bg-forest-800 text-gold-400 border-forest-800" : "bg-white text-forest-700 border-forest-100"}`}
                    >
                        {t}
                    </button>
                ))}
            </div>
            {reviews.length === 0 ? (
                <p data-testid="reviews-empty" className="text-sm text-forest-900/60">No {tab} reviews.</p>
            ) : (
                <div className="space-y-4">
                    {reviews.map((r) => (
                        <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white rounded-2xl border border-forest-100 p-5 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <Stars value={r.rating} />
                                    <span className="font-bold text-forest-900 text-sm">{r.name}</span>
                                    {r.city && <span className="text-xs text-forest-700/70">{r.city}</span>}
                                    {r.verified && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-forest-700">
                                            <CircleCheck className="w-3.5 h-3.5" /> Verified #{r.order_number}
                                        </span>
                                    )}
                                </div>
                                <span className="text-xs text-forest-700/60">{r.product_name} · {new Date(r.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-forest-900/80 mt-3">{r.text}</p>
                            {r.photo_urls?.length > 0 && (
                                <div className="flex gap-2 mt-3">
                                    {r.photo_urls.map((u) => (
                                        <a key={u} href={`${API_BASE}${u}`} target="_blank" rel="noreferrer">
                                            <img src={`${API_BASE}${u}`} alt="Review" className="w-20 h-20 rounded-lg object-cover border border-forest-100" />
                                        </a>
                                    ))}
                                </div>
                            )}
                            <div className="flex gap-2 mt-4">
                                {r.status !== "approved" && (
                                    <button data-testid={`approve-review-${r.id}`} onClick={() => setStatus(r.id, "approved")} className="inline-flex items-center gap-1 bg-forest-800 text-gold-400 text-xs font-bold px-4 py-2 rounded-lg">
                                        <Check className="w-3.5 h-3.5" /> Approve
                                    </button>
                                )}
                                {r.status !== "rejected" && (
                                    <button data-testid={`reject-review-${r.id}`} onClick={() => setStatus(r.id, "rejected")} className="inline-flex items-center gap-1 bg-white border border-forest-200 text-forest-800 text-xs font-bold px-4 py-2 rounded-lg">
                                        <X className="w-3.5 h-3.5" /> Reject
                                    </button>
                                )}
                                {r.status === "approved" && r.photo_urls?.length > 0 && (
                                    <label className="inline-flex items-center gap-2 text-xs font-bold text-forest-800 px-3 py-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            data-testid={`gallery-toggle-${r.id}`}
                                            checked={!!r.in_gallery}
                                            onChange={() => toggleGallery(r)}
                                            className="accent-forest-800 w-4 h-4"
                                        />
                                        Show in gallery{mode === "all" && " (used in 'Only selected' mode)"}
                                    </label>
                                )}
                                <button data-testid={`delete-review-${r.id}`} onClick={() => remove(r.id)} className="inline-flex items-center gap-1 text-rose-700 text-xs font-bold px-3 py-2 rounded-lg hover:bg-rose-50">
                                    <Trash2 className="w-3.5 h-3.5" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
